import { gql } from "@apollo/client";
import * as fragment from "./fragments";

export const GROUPED_ASSETS_QUERY = gql`
  query GroupedAssets($projectId: ID!, $sortBy: String, $sortDirection: String, $page: Int, $limit: Int, $search: String, $hideDepletedAssets: Boolean, $warehouseId: String) {
    groupedAssets(projectId: $projectId, sortBy: $sortBy, sortDirection: $sortDirection, page: $page, limit: $limit, search: $search, hideDepletedAssets: $hideDepletedAssets, warehouseId: $warehouseId) {
      collection {
        manufacturer
        associatedItemIds
        associatedAssetIds
        skuNumber
        description
        availableQuantity
      }
      metadata {
        ${fragment.PAGINATION_FRAGMENT}
      }
    }
  }
`;

export const FETCH_USER = gql`
  query fetchUser($id: ID) {
    user(id: $id) {
      ${fragment.USER_FRAGMENT}
      clientIds
      currentSignInAt
      active
    }
  }
`;

export const FETCH_AUTH_USER_QUERY = gql`
  query {
    user {
      ${fragment.USER_FRAGMENT}
    }
  }
`;

export const PDF_SIGNED_URL_QUERY = gql`
  query PdfSignedUrl($resourceType: String!, $resourceId: ID!, $pathSuffix: String) {
    pdfSignedUrl(resourceType: $resourceType, resourceId: $resourceId, pathSuffix: $pathSuffix)
  }
`;

export const RELEASES_QUERY = gql`
  query($page: Int, $limit: Int) {
    releases(page: $page, limit: $limit) {
      collection {
        id
        slug
        title
        body
        publishedAt
      }
      metadata {
        ${fragment.PAGINATION_FRAGMENT}
      }
    }
  }
`;

export const USER_COST_CENTERS_QUERY = gql`
  query getUserCostCenters {
    userCostCenters {
      ${fragment.SIMPLE_COST_CENTER_FRAGMENT}
      project {
        id
        name
      }
    }
  }
`;

export const USER_BLACKOUT_DATES_QUERY = gql`
  query {
    user {
      id
      company {
        id
        blackoutDates
        __typename
      }
      __typename
    }
  }
`;

export const USER_COMPANY_INTERNAL_WORK_ORDER_TYPES_QUERY = gql`
  query {
    user {
      id
      company {
        id
        internalWorkOrderTypes
        __typename
      }
      __typename
    }
  }
`;

export const FETCH_SHORT_USER = gql`
  query InvitationUser($accessToken: String) {
    invitationUser(accessToken: $accessToken) {
      email
      invitationAccepted
    }
  }
`;

export const ASSETS_COLLECTION_QUERY = gql`
  query GetAssetsCollection(
    $clientId: ID
    $projectId: ID
    $inventoryRequestId: ID
    $search: String
    $categories: [String!]
    $page: Int
    $limit: Int
    $storePage: Boolean
    $type: String
    $types: [String!]
    $warehouseId: String
    $warehouseLocationName: String
    $mobileWarehouseLocationName: String
    $hasImportedAssets: Boolean
    $showDeployedAssets: Boolean
    $excludeReceivedAssets: Boolean
    $itemId: String
    $hideDepletedAssets: Boolean
    ) {
    assetsCollection(
      clientId: $clientId
      projectId: $projectId
      inventoryRequestId: $inventoryRequestId
      search: $search
      categories: $categories
      page: $page
      limit: $limit
      storePage: $storePage
      type: $type
      types: $types
      warehouseId: $warehouseId
      warehouseLocationName: $warehouseLocationName
      mobileWarehouseLocationName: $mobileWarehouseLocationName
      hasImportedAssets: $hasImportedAssets
      showDeployedAssets: $showDeployedAssets
      excludeReceivedAssets: $excludeReceivedAssets
      itemId: $itemId
      hideDepletedAssets: $hideDepletedAssets
      ) {
      collection {
        ${fragment.ASSET_FRAGMENT}
        ${fragment.ASSET_MEDICAL_FRAGMENT}
      }
      metadata {
        ${fragment.PAGINATION_FRAGMENT}
      }
    }
    categories(projectId: $projectId)
  }
`;

export const LOCATION_SNAPSHOT_ASSETS_QUERY = gql`
  query GetLocationSnapshotAssets(
    $warehouseLocationId: ID
    $mobileWarehouseLocationId: ID
  ) {
    assetsCollection(
      warehouseLocationId: $warehouseLocationId
      mobileWarehouseLocationId: $mobileWarehouseLocationId
    ) {
      collection {
        id
        assetId
        itemId
        photo
        description
        model
        dimensions
        rackLocations
        shipmentItems {
          id
          quantity
          shipment {
            id
            shipmentId
          }
        }
        project {
          id
          name
          shipmentItemPdfPreferences
        }
      }
    }
  }
`;

export const ASSETS_COLLECTION_INVENTORY_MOVE_QUERY = gql`
  query GetAssetsCollection(
    $clientId: ID
    $projectId: ID
    $inventoryRequestId: ID
    $search: String
    $exactMatchOnly: Boolean
    $categories: [String!]
    $page: Int
    $limit: Int
    $order: JSON
    $storePage: Boolean
    $type: String
    $types: [String!]
    $warehouseId: String
    $warehouseLocationName: String
    $mobileWarehouseLocationName: String
    $hasImportedAssets: Boolean
    $showDeployedAssets: Boolean
    $excludeReceivedAssets: Boolean
    $itemId: String
    $hideDepletedAssets: Boolean
    ) {
    assetsCollection(
      clientId: $clientId
      projectId: $projectId
      inventoryRequestId: $inventoryRequestId
      search: $search
      exactMatchOnly: $exactMatchOnly
      categories: $categories
      page: $page
      limit: $limit
      order: $order
      storePage: $storePage
      type: $type
      types: $types
      warehouseId: $warehouseId
      warehouseLocationName: $warehouseLocationName
      mobileWarehouseLocationName: $mobileWarehouseLocationName
      hasImportedAssets: $hasImportedAssets
      showDeployedAssets: $showDeployedAssets
      excludeReceivedAssets: $excludeReceivedAssets
      itemId: $itemId
      hideDepletedAssets: $hideDepletedAssets
      ) {
      collection {
        ${fragment.SIMPLE_ASSET_FRAGMENT}
      }
      metadata {
        ${fragment.PAGINATION_FRAGMENT}
      }
    }
    categories(projectId: $projectId)
  }
`;

export const ASSETS_COLLECTION_TABLE_QUERY = gql`
  query GetAssetsTableCollection(
    $clientId: ID
    $projectId: ID
    $inventoryRequestId: ID
    $search: String
    $inventoryStatus: String
    $trackingStatus: String
    $categories: [String!]
    $page: Int
    $limit: Int
    $order: JSON
    $hasMissingItems: Boolean
    $storePage: Boolean
    $type: String
    $types: [String!]
    $hasImportedAssets: Boolean
    $showDeployedAssets: Boolean
    $excludeReceivedAssets: Boolean
    $itemId: String
    $hideDepletedAssets: Boolean
    $warehouseId: String
    $costCenterId: ID
    ) {
    assetsCollection(
      clientId: $clientId
      projectId: $projectId
      inventoryRequestId: $inventoryRequestId
      inventoryStatus: $inventoryStatus
      trackingStatus: $trackingStatus
      search: $search
      categories: $categories
      page: $page
      limit: $limit
      order: $order
      hasMissingItems: $hasMissingItems
      storePage: $storePage
      type: $type
      types: $types
      hasImportedAssets: $hasImportedAssets
      showDeployedAssets: $showDeployedAssets
      excludeReceivedAssets: $excludeReceivedAssets
      itemId: $itemId
      hideDepletedAssets: $hideDepletedAssets
      warehouseId: $warehouseId
      costCenterId: $costCenterId
      ) {
      collection {
        ${fragment.ASSET_FRAGMENT}
        ${fragment.ASSET_MEDICAL_FRAGMENT}
      }
      metadata {
        ${fragment.PAGINATION_FRAGMENT}
      }
    }
  }
`;

// Ids-only companion of ASSETS_COLLECTION_TABLE_QUERY. `totalIds` (every matching
// asset id, 140k+ for the largest tenants) used to ride along on every page load
// so "select all" could use it later; it is now fetched only when select-all is clicked.
export const ASSETS_COLLECTION_IDS_QUERY = gql`
  query GetAssetsTableCollectionIds(
    $clientId: ID
    $projectId: ID
    $inventoryRequestId: ID
    $search: String
    $inventoryStatus: String
    $trackingStatus: String
    $categories: [String!]
    $page: Int
    $limit: Int
    $order: JSON
    $hasMissingItems: Boolean
    $storePage: Boolean
    $type: String
    $types: [String!]
    $hasImportedAssets: Boolean
    $showDeployedAssets: Boolean
    $excludeReceivedAssets: Boolean
    $itemId: String
    $hideDepletedAssets: Boolean
    $warehouseId: String
    $costCenterId: ID
    ) {
    assetsCollection(
      clientId: $clientId
      projectId: $projectId
      inventoryRequestId: $inventoryRequestId
      inventoryStatus: $inventoryStatus
      trackingStatus: $trackingStatus
      search: $search
      categories: $categories
      page: $page
      limit: $limit
      order: $order
      hasMissingItems: $hasMissingItems
      storePage: $storePage
      type: $type
      types: $types
      hasImportedAssets: $hasImportedAssets
      showDeployedAssets: $showDeployedAssets
      excludeReceivedAssets: $excludeReceivedAssets
      itemId: $itemId
      hideDepletedAssets: $hideDepletedAssets
      warehouseId: $warehouseId
      costCenterId: $costCenterId
      ) {
      metadata {
        totalIds
      }
    }
  }
`;

export const ASSETS_FIND_REPLACE_COLLECTION_QUERY = gql`
  query GetAssetsFindReplaceCollection(
    $clientId: ID
    $projectId: ID
    $inventoryRequestId: ID
    $search: String
    $inventoryStatus: String
    $trackingStatus: String
    $categories: [String!]
    $page: Int
    $limit: Int
    $order: JSON
    $hasMissingItems: Boolean
    $storePage: Boolean
    $type: String
    $types: [String!]
    $hasImportedAssets: Boolean
    $showDeployedAssets: Boolean
    $excludeReceivedAssets: Boolean
    $itemId: String
    $hideDepletedAssets: Boolean
    $warehouseId: String
    $costCenterId: ID
  ) {
    assetsCollection(
      clientId: $clientId
      projectId: $projectId
      inventoryRequestId: $inventoryRequestId
      inventoryStatus: $inventoryStatus
      trackingStatus: $trackingStatus
      search: $search
      categories: $categories
      page: $page
      limit: $limit
      order: $order
      hasMissingItems: $hasMissingItems
      storePage: $storePage
      type: $type
      types: $types
      hasImportedAssets: $hasImportedAssets
      showDeployedAssets: $showDeployedAssets
      excludeReceivedAssets: $excludeReceivedAssets
      itemId: $itemId
      hideDepletedAssets: $hideDepletedAssets
      warehouseId: $warehouseId
      costCenterId: $costCenterId
    ) {
      collection {
        id
        description
      }
      metadata {
        totalCount
      }
    }
  }
`;

export const ASSETS_COLLECTION_BY_IDS_QUERY = gql`
  query GetAssetsByIds($assetIds: [ID!]!) {
    assetsCollection(assetIds: $assetIds) {
      collection {
        id
        assetId
        itemId
        rackLocations
        warehousesFromRackLocations {
          id
          warehouseId
          name
        }
      }
    }
  }
`;

export const ONSITE_MANIFEST_COLLECTION_QUERY = gql`
  query GetOnsiteManifestCollection(
    $projectId: ID
    $search: String
    $order: JSON
    $page: Int
    $limit: Int
    $pendingValidation: Boolean
  ) {
    assetsCollection(
      projectId: $projectId
      search: $search
      order: $order
      page: $page
      limit: $limit
      types: ["Sku"]
      pendingValidation: $pendingValidation
    ) {
      collection {
        ${fragment.ASSET_FRAGMENT}
        ${fragment.ASSET_MEDICAL_FRAGMENT}
        validationListValidated
      }
      metadata {
        ${fragment.PAGINATION_FRAGMENT}
      }
    }
  }
`;

export const ONSITE_INVENTORY_COLLECTION_QUERY = gql`
  query GetOnsiteInventoryCollection(
    $clientId: ID
    $projectId: ID
    $search: String
    $order: JSON
    $type: String
    $types: [String!]
    $hasImportedAssets: Boolean
    $hideDepletedAssets: Boolean
  ) {
    assetsCollection(
      clientId: $clientId
      projectId: $projectId
      search: $search
      order: $order
      type: $type
      types: $types
      hasImportedAssets: $hasImportedAssets
      hideDepletedAssets: $hideDepletedAssets
    ) {
      collection {
        id
        type
        itemId
        assetId
        barcode
        description
        category
        manufacturer
        model
        quantity
        availableQuantity
        skuNumber
        images {
          url
          id
          default
        }
      }
      metadata {
        totalCount
      }
    }
  }
`;

export const ASSETS_COLLECTION_INVENTORY_ITEMS_QUERY = gql`
  query GetAssetsCollectionInventoryItems(
    $clientId: ID
    $projectId: ID
    $inventoryRequestId: ID
    $search: String
    $inventoryStatus: String
    $trackingStatus: String
    $categories: [String!]
    $page: Int
    $limit: Int
    $order: JSON
    $hasMissingItems: Boolean
    $storePage: Boolean
    $type: String
    $types: [String!]
    $hasImportedAssets: Boolean
    $showDeployedAssets: Boolean
    $excludeReceivedAssets: Boolean
    $itemId: String
    $hideDepletedAssets: Boolean
    $warehouseId: String
    $costCenterId: ID
  ) {
    assetsCollection(
      clientId: $clientId
      projectId: $projectId
      inventoryRequestId: $inventoryRequestId
      inventoryStatus: $inventoryStatus
      trackingStatus: $trackingStatus
      search: $search
      categories: $categories
      page: $page
      limit: $limit
      order: $order
      hasMissingItems: $hasMissingItems
      storePage: $storePage
      type: $type
      types: $types
      hasImportedAssets: $hasImportedAssets
      showDeployedAssets: $showDeployedAssets
      excludeReceivedAssets: $excludeReceivedAssets
      itemId: $itemId
      hideDepletedAssets: $hideDepletedAssets
      warehouseId: $warehouseId
      costCenterId: $costCenterId
    ) {
      collection {
        id
        inventoryItems {
          quantity
          inventoryRequest {
            workOrderNumber
          }
        }
      }
    }
  }
`;

export const ASSETS_PROJECT_IMAGES_QUERY = gql`
  query GetAssetsProjectImages(
    $clientId: ID
    $projectId: ID
    $inventoryRequestId: ID
    $search: String
    $inventoryStatus: String
    $trackingStatus: String
    $categories: [String!]
    $page: Int
    $limit: Int
    $order: JSON
    $hasMissingItems: Boolean
    $storePage: Boolean
    $types: [String!]
    ) {
    assetsCollection(
      clientId: $clientId
      projectId: $projectId
      inventoryRequestId: $inventoryRequestId
      inventoryStatus: $inventoryStatus
      trackingStatus: $trackingStatus
      search: $search
      categories: $categories
      page: $page
      limit: $limit
      order: $order
      hasMissingItems: $hasMissingItems
      storePage: $storePage
      types: $types
      ) {
      collection {
        itemId
        manufacturer
        description
        disposition
        dimensions
        quantity
        images {
          ${fragment.ASSET_IMAGE_FRAGMENT}
        }
      }
      metadata {
        ${fragment.PAGINATION_FRAGMENT}
        totalIds
      }
    }
  }
`;

export const KITS_COLLECTION_TABLE_QUERY = gql`
  query KitsCollection(
    $projectId: ID
    $clientId: ID
    $search: String
    $page: Int
    $limit: Int
    $order: JSON
    ) {
    kitsCollection(
      projectId: $projectId
      clientId: $clientId
      search: $search
      page: $page
      limit: $limit
      order: $order
      ) {
      collection {
        ${fragment.KIT_FRAGMENT}
      }
      metadata {
        ${fragment.PAGINATION_FRAGMENT}
        totalIds
      }
    }
  }
`;

export const KIT_ASSETS_QUERY = gql`
  query GetAsset($id: ID!) {
    asset(id: $id) {
      active
      ${fragment.ASSET_FRAGMENT}
      groupedKitAssets
      kitAssets {
        id
        kit {
          ${fragment.ASSET_FRAGMENT}
        } 
        asset {
          ${fragment.ASSET_FRAGMENT}
        }
        quantity
      }
    }
  }
`;

export const GROUPED_ASSET_QUERY = gql`
  query GetAsset($id: ID!) {
    asset(id: $id) {
      active
      ${fragment.ASSET_FRAGMENT}
      groupedMemberAssets
      groupedAssetMembers {
        id
        quantity
        asset {
          ${fragment.ASSET_FRAGMENT}
        }
      }
    }
  }
`;

export const ASSETS_COLLECTION_RESTOCK_INVENTORY_QUERY = gql`
  query GetAssetsCollectionRestockInventory(
     $projectId: ID
     $clientId: ID
     $search: String
     $type: String
     $types: [String!]
    ) {
    assetsCollection(
      projectId: $projectId
      clientId: $clientId
      search: $search
      type: $type
      types: $types
      ) {
      collection {
        id
        itemId
        assetId
        rackLocations
        description
        model
        availableQuantity
        images {
          ${fragment.ASSET_IMAGE_FRAGMENT}
        }
      }
    }
  }
`;

export const LOOKUPS_QUERY = gql`
  query GetLookups($projectId: ID) {
    lookups(projectId: $projectId) {
      descriptions
      colors
      manufacturers
      dispositions
    }
  }
`;

export const CLIENTS_QUERY = gql`
  query getClients {
    clients {
      ${fragment.CLIENT_FRAGMENT}
    }
  }
`;

export const CLIENTS_AND_PROJECTS_QUERY = gql`
  query getClients($manage: Boolean) {
    clients(manage: $manage) {
      id
      name
      branchCode
      disabled
      projects {
        id
        name
        active
        settings
        costCenters {
          id
          active
          disabled
          costCenterName
          costCenterCode
        }
      }
    }
  }
`;

export const CLIENT_PROJECT_OPTIONS_QUERY = gql`
  query getClientProjectOptions {
    clients {
      id
      name
      disabled
      projects {
        id
        name
        active
      }
    }
  }
`;

export const PROJECTS_QUERY = gql`
  query getProjects {
    projects(storePage: true) {
      ${fragment.PROJECT_FRAGMENT}
    }
  }
`;

export const BULK_TRANSFER_PROJECT_OPTIONS_QUERY = gql`
  query getBulkTransferProjectOptions {
    projects(storePage: true) {
      id
      name
      active
      client {
        id
        name
      }
    }
  }
`;

export const PROJECTS_NAMES_QUERY = gql`
  query getProjects($manage: Boolean) {
    projects(storePage: true, manage: $manage) {
      id
      name
      active
      client {
        id
        name
      }
    }
  }
`;

export const CLIENTS_TABLE_QUERY = gql`
  query getClientsTable($search: String) {
    clients(manage: true, search: $search) {
      id
      name
      active
      createdAt
      disabled
      projectsCount
      projects {
        id
        name
        active
      }
    }
  }
`;

export const PROJECTS_TABLE_QUERY = gql`
  query getProjectsTable($search: String) {
    projects(manage: true, search: $search) {
      id
      name
      active
      createdAt
      client {
        id
        active
        name
      }
    }
  }
`;

export const MOVEWARE_JOB_QUERY = gql`
  query MovewareJob($id: ID!) {
    movewareJob(id: $id) {
      job
      storageAccounts
      activities
    }
  }
`;

export const MOVEWARE_ACTIVITIES_QUERY = gql`
  query MovewareActivities(
    $projectId: ID!
    $date: ISO8601Date!
    $dateFrom: ISO8601Date
    $dateTo: ISO8601Date
    $searchId: String
    $activityType: String
  ) {
    movewareActivities(
      projectId: $projectId
      date: $date
      dateFrom: $dateFrom
      dateTo: $dateTo
      searchId: $searchId
      activityType: $activityType
    ) {
      customerId
      activities
    }
  }
`;

export const MOVEWARE_STORAGE_ACCOUNT_QUERY = gql`
  query MovewareStorageAccount($id: ID!) {
    movewareStorageAccount(id: $id) {
      externalRecordId
      storageAccountId
      displayName
      status
      eligible
      projectName
      storageAccountName
      branch
      branchName
      group
      notes
      orderNumber
      moveId
      dateIn
      volumeCubicFeet
      volumeCubicMeters
      weightPounds
      weightKilograms
      customerId
      customerName
      customerNumber
      customerStatus
      alreadyLinked
      linkedProject {
        id
        name
      }
      matchedClient {
        id
        name
        branchCode
      }
    }
  }
`;

export const LINKABLE_EXTERNAL_RECORDS_QUERY = gql`
  query LinkableExternalRecords($linkableType: String!, $linkableId: ID!) {
    linkableExternalRecords(linkableType: $linkableType, linkableId: $linkableId) {
      id
      recordType
      externalId
      displayName
      data
    }
  }
`;

export const PROJECT_INTEGRATION_RECORDS_QUERY = gql`
  query ProjectIntegrationRecords($id: ID!) {
    project(id: $id) {
      id
      externalRecords {
        id
        recordType
        externalId
        displayName
        status
        data
      }
    }
  }
`;

export const INTEGRATION_REVIEW_QUERY = gql`
  query IntegrationReview($provider: String!, $reviewState: String!, $limit: Int) {
    integrationSyncRuns(provider: $provider, limit: 1) {
      id
      status
      startedAt
      finishedAt
      totalCount
      processedCount
      newCount
      changedCount
      unmatchedCount
      closedCount
      missingCount
      errorCount
      errorMessage
    }
    integrationExternalRecords(
      provider: $provider
      reviewState: $reviewState
      limit: $limit
    ) {
      id
      externalId
      displayName
      status
      data
      lastSeenAt
      lastChangedAt
      missingSince
      linkedProject {
        id
        name
      }
    }
  }
`;

export const FAILED_BILLING_VALIDATION_PROJECTS_QUERY = gql`
  query getFailedBillingValidationProjects($clientId: ID, $warehouseId: String) {
    projects(failedProjectBillingValidation: true, clientId: $clientId, warehouseId: $warehouseId) {
      id
      name
    }
  }
`;

export const BILLING_VALIDATION_FAILURES_QUERY = gql`
  query getBillingValidationFailures(
    $type: String
    $clientId: Int
    $projectId: Int
    $warehouseId: String
    $page: Int
    $limit: Int
    $orderBy: JSON
    $dateRangeDays: Int
    $includeAllDates: Boolean
  ) {
    billingValidationFailures(
      type: $type
      clientId: $clientId
      projectId: $projectId
      warehouseId: $warehouseId
      page: $page
      limit: $limit
      orderBy: $orderBy
      dateRangeDays: $dateRangeDays
      includeAllDates: $includeAllDates
    ) {
      totalCount
      totalPages
      currentPage
      perPage
      data {
        ... on InventoryRequest {
          __typename
          id
          uid
          workOrderNumber
          billingValidationStatus
          billingValidationDetails
          createdAt
          projectName
          projectId
          deliveryDate
          vaStatusDetailed
          user {
            email
          }
        }
        ... on InternalWorkOrder {
          __typename
          id
          uid
          internalWorkOrderNumber
          billingValidationStatus
          billingValidationDetails
          createdAt
          deadlineDate
          user {
            email
          }
          project {
            id
            name
          }
        }
        ... on Shipment {
          __typename
          id
          shipmentId
          billingValidationStatus
          billingValidationDetails
          createdAt
          completeTime
          completer {
            email
            name
          }
          user {
            email
            name
          }
          project {
            id
            name
          }
        }
        ... on ShipmentItem {
          __typename
          id
          billingValidationStatus
          billingValidationDetails
          createdAt
          shipment {
            id
            shipmentId
            completer {
              email
              name
            }
            user {
              email
              name
            }
            project {
              id
              name
            }
          }
        }
        ... on Asset {
          __typename
          id
          assetId
          billingValidationStatus
          billingValidationDetails
          createdAt
          project {
            id
            name
          }
        }
      }
    }
  }
`;

export const BARCODE_SCANS_QUERY = gql`
  query BarcodeScans($assetId: ID!) {
    barcodeScans(assetId: $assetId) {
      ${fragment.BARCODE_SCAN_FRAGMENT}
    }
  }
`;

export const USERS_TABLE_QUERY = gql`
  query getUsersTable($search: String, $active: Boolean) {
    users(search: $search, active: $active) {
      id
      email
      active
      name
      role
      createdAt
      currentSignInAt
      clientsQuantity
      projectsQuantity
      invitationAccepted
    }
  }
`;

export const CLIENT_USERS = gql`
  query GetClientUsers($clientId: ID!) {
    clientUsers(clientId: $clientId) {
      id
      email
      name
      role
      createdAt
    }
  }
`;

export const COMPANY_USERS_QUERY = gql`
  query {
    companyUsers {
      id
      email
      role
      name
    }
  }
`;

export const ASSET_BY_BARCODE_QUERY = gql`
  query AssetByBarcode($barcode: String, $projectId: ID, $clientId: ID) {
    asset(barcode: $barcode, projectId: $projectId, clientId: $clientId) {
       ${fragment.ASSET_FRAGMENT}
    }
  }
`;

export const ASSET_QUERY = gql`
  query GetAsset($id: ID!) {
    asset(id: $id) {
      active
      ${fragment.ASSET_FRAGMENT}
      ${fragment.ASSET_MEDICAL_FRAGMENT}
      inventoryItems {
        quantity
        inventoryRequest {
          workOrderNumber
        }
      }
      nextAssetId
      prevAssetId
    }
  }
`;

export const ASSET_VARIANTS_QUERY = gql`
  query GetAssetVariants($id: ID!) {
    asset(id: $id) {
      id
      variants {
        ${fragment.ASSET_FRAGMENT}
      }
    }
  }
`;

export const FAVORITE_REPORTS_QUERY = gql`
  query FavoriteReports($projectId: ID, $clientId: ID, $userReportsOnly: Boolean) {
    favoriteReports(projectId: $projectId, clientId: $clientId, userReportsOnly: $userReportsOnly) {
      ${fragment.FAVORITE_REPORTS_FRAGMENT}
    }
  }
`;

export const NOTIFICATIONS_QUERY = gql`
  query Notifications($limit: Int, $projectId: ID, $clientId: ID, $type: String, $ownerId: ID) {
    notifications(
      limit: $limit
      projectId: $projectId
      clientId: $clientId
      type: $type
      ownerId: $ownerId
    ) {
      id
      title
      client {
        id
        name
      }
      project {
        id
        name
      }
      message
      category
      link
      createdAt
      userEmail
      viewed
      ownerType
      ownerId
      context
      favoriteReport {
        id
        payload
        reportNotes
        createdAt
        user {
          email
          name
        }
      }
    }
  }
`;

export const INVENTORY_REQUESTS_QUERY = gql`
  query InventoryRequests($limit: Int, $page: Int, $type: String, $projectId: Int, $search: String, $sortBy: [JSON!], $dateRange: JSON) {
    inventoryRequests(page: $page, limit: $limit, type: $type, projectId: $projectId, search: $search, sortBy: $sortBy, dateRange: $dateRange) {
      collection {
        ${fragment.DETAILED_INVENTORY_REQUEST_FRAGMENT}
      }
      metadata {
        ${fragment.PAGINATION_FRAGMENT}
      }
    }
  }
`;

export const INVENTORY_REQUEST_QUERY = gql`
  query inventoryRequest($id: ID) {
    inventoryRequest(id: $id) {
      ${fragment.OPTIMIZED_INVENTORY_REQUEST_FRAGMENT}
    }
  }
`;

// Lightweight on-demand fetch of the va_status audit trail for a single
// inventory request. List/work-order queries omit vaStatusDetailed (it's
// expensive to resolve per row); the VaStatus dropdown fetches it lazily via
// this query only when the user opens the menu.
export const INVENTORY_REQUEST_VA_STATUS_DETAILED_QUERY = gql`
  query inventoryRequestVaStatusDetailed($id: ID) {
    inventoryRequest(id: $id) {
      id
      vaStatus
      vaStatusDetailed
    }
  }
`;

export const INVENTORY_REQUESTS_WITH_PAGINATION_QUERY = gql`
  query InventoryRequests($limit: Int, $page: Int, $type: String,  $search: String, $sortBy: [JSON!], $dateRange: JSON) {
    inventoryRequests(page: $page, limit: $limit, type: $type, search: $search, sortBy: $sortBy, dateRange: $dateRange) {
      collection {
        ${fragment.DETAILED_INVENTORY_REQUEST_FRAGMENT}
      }
      metadata {
        ${fragment.PAGINATION_FRAGMENT}
      }
    }
  }
`;

export const SIMPLE_INVENTORY_REQUESTS_QUERY = gql` 
  query InventoryRequests($limit: Int, $page: Int, $type: String, $search: String, $itemId: String, $sortBy: [JSON!], $dateRange: JSON, $projectId: Int, $clientId: Int, $warehouseId: String, $statusFilter: String) {
    inventoryRequests(page: $page, limit: $limit, type: $type, search: $search, itemId: $itemId, sortBy: $sortBy, dateRange: $dateRange, projectId: $projectId, clientId: $clientId, warehouseId: $warehouseId, statusFilter: $statusFilter) {
      collection {
        ${fragment.SIMPLE_INVENTORY_REQUEST_FRAGMENT}
      }
      metadata {
        ${fragment.PAGINATION_FRAGMENT}
      }
    }
  }
`;

export const SIMPLE_CALENDAR_INVENTORY_REQUESTS_QUERY = gql` 
  query InventoryRequests($limit: Int, $page: Int, $type: String, $search: String, $sortBy: [JSON!], $dateRange: JSON, $projectId: Int, $clientId: Int, $warehouseId: String) {
    inventoryRequests(page: $page, limit: $limit, type: $type, search: $search, sortBy: $sortBy, dateRange: $dateRange, projectId: $projectId, clientId: $clientId, warehouseId: $warehouseId) {
      collection {
        ${fragment.SIMPLE_CALENDAR_INVENTORY_REQUEST_FRAGMENT}
      }
      metadata {
        ${fragment.PAGINATION_FRAGMENT}
      }
    }
  }
`;

export const CALENDAR_DAY_VIEW_QUERY = gql` 
  query InventoryRequests($limit: Int, $page: Int, $type: String, $search: String, $sortBy: [JSON!], $dateRange: JSON, $projectId: Int, $clientId: Int, $warehouseId: String) {
    inventoryRequests(page: $page, limit: $limit, type: $type, search: $search, sortBy: $sortBy, dateRange: $dateRange, projectId: $projectId, clientId: $clientId, warehouseId: $warehouseId) {
      collection {
        ${fragment.CALENDAR_INVENTORY_REQUEST_WITH_PROJECT_NAME_FRAGMENT}
      }
      metadata {
        ${fragment.PAGINATION_FRAGMENT}
      }
    }
  }
`;

export const CALENDAR_DAY_VIEW_WORK_ORDERS_QUERY = gql`
  query WorkOrders($limit: Int, $page: Int, $type: String, $search: String, $dateRange: JSON, $projectId: Int, $clientId: Int, $warehouseId: String, $sortField: String, $sortDirection: String) {
    workOrders(page: $page, limit: $limit, type: $type, search: $search, dateRange: $dateRange, projectId: $projectId, clientId: $clientId, warehouseId: $warehouseId, sortField: $sortField, sortDirection: $sortDirection) {
      collection {
        __typename
        ... on InventoryRequest {
          ${fragment.CALENDAR_INVENTORY_REQUEST_WITH_PROJECT_NAME_FRAGMENT}
        }
        ... on InternalWorkOrder {
          id
          uid
          internalWorkOrderNumber
          comment
          deadlineDate
          createdAt
          vaStatus
          user { id email }
          project { id name }
        }
      }
      metadata {
        ${fragment.PAGINATION_FRAGMENT}
      }
    }
  }
`;

export const MOVE_MATRICES_QUERY = gql`
  query GetMoveMatrices($projectId: ID, $clientId: ID) {
    moveMatrices(projectId: $projectId, clientId: $clientId) {
      ${fragment.MOVE_MATRIX_FRAGMENT}
    }
  }
`;

export const SUBSCRIPTION_QUERY = gql`
  query {
    companySubscription {
      id
      status
      planStartAt
      planEndAt
      companyId
      companyPlan {
        id
        name
        amount
        description
      }
    }
  }
`;

export const COMPANY_PLANS_QUERY = gql`
  query {
    companyPlans {
      id
      name
      amount
      description
    }
  }
`;

export const SHOPPING_INVENTORY_REQUEST_QUERY = gql`
  query {
    shoppingInventoryRequest {
      ${fragment.SIMPLE_SHOPPING_INVENTORY_REQUEST_FRAGMENT}
    }
  }
`;

export const SHOPPING_INVENTORY_CARTS_QUERY = gql`
  query {
    shoppingInventoryCarts {
      ${fragment.SIMPLE_SHOPPING_INVENTORY_REQUEST_FRAGMENT}
    }
  }
`;

export const SHOPPING_INVENTORY_ITEMS_QUANTIY = gql`
  query {
    shoppingInventoryCarts {
      inventoryItems {
        quantity
      }
    }
  }
`;

export const SHOPPING_INVENTORY_REQUESTS_COLLECTION_QUERY = gql`
  query GetInventoryRequestsCollection($limit: Int, $page: Int, $type: String,  $search: String, $sortBy: [JSON!], $dateRange: JSON) {
    shoppingInventoryRequestsCollection(page: $page, limit: $limit, type: $type, search: $search, sortBy: $sortBy, dateRange: $dateRange) {
      collection {
        ${fragment.SIMPLE_INVENTORY_REQUEST_FRAGMENT}
      }
      metadata {
        ${fragment.PAGINATION_FRAGMENT}
      }
    }
  }
`;

export const FETCH_PROJECT_QUERY = gql`
  query GetProject($id: ID!) {
    project(id: $id) {
      ${fragment.PROJECT_FRAGMENT}
    }
  }
`;

export const FETCH_CLIENT_QUERY = gql`
  query GetClient($id: ID!) {
    client(id: $id) {
      ${fragment.CLIENT_FRAGMENT}
      clientsWarehouses {
        warehouse {
          id
        }
      }
    }
  }
`;

export const SHOPPING_CART_INVENTORY_REQUEST_QUERY = gql`
  query GetShoppingInventoryRequest {
    shoppingInventoryRequest {
      ${fragment.SIMPLE_SHOPPING_INVENTORY_REQUEST_FRAGMENT}
    }
  }
`;

// Fetches the metadata needed to render the order confirmation page:
// project logo and client vendor name, resolved through the inventory request.
export const ORDER_CONFIRMATION_METADATA_QUERY = gql`
  query OrderConfirmationMetadata($id: ID) {
    inventoryRequest(id: $id) {
      id
      inventoryItems {
        project {
          id
          selectedMainLogoUrl
          customerLogoUrl
          client {
            settings
          }
        }
      }
    }
  }
`;

export const INVENTORY_TRANSACTIONS_QUERY = gql`
  query {
    inventoryTransactions {
      quantity
      createdAt
    }
  }
`;

export const ASSET_ATTACHMENTS_QUERY = gql`
  query GetAssetAttachments($assetId: ID!) {
    assetAttachments(assetId: $assetId) {
      ${fragment.ATTACHMENT_FRAGMENT}
    }
  }
`;

export const ACCESS_LINK_QUERY = gql`
  query GetAccessLink($id: ID!) {
    accessLink(id: $id) {
      ${fragment.ACCESS_LINK_FRAGMENT}
    }
  }
`;

export const ACCESS_LINKS_QUERY = gql`
  query {
    accessLinks {
      ${fragment.ACCESS_LINK_FRAGMENT}
    }
  }
`;

export const INVENTORY_REQUEST_ATTACHMENT_MUTATION = gql`
  query InventoryRequestAttachments($inventoryRequestId: ID!) {
    inventoryRequestAttachments(inventoryRequestId: $inventoryRequestId) {
      ${fragment.ATTACHMENT_FRAGMENT}
    }
  }
`;

export const INVENTORY_REQUEST_FINAL_RELEASE_ATTACHMENTS_QUERY = gql`
  query InventoryRequestFinalReleaseAttachments($inventoryRequestId: ID!) {
    inventoryRequestFinalReleaseAttachments(inventoryRequestId: $inventoryRequestId) {
      ${fragment.ATTACHMENT_FRAGMENT}
    }
  }
`;

export const INTERNAL_WORK_ORDER_ATTACHMENTS_QUERY = gql`
  query InternalWorkOrderAttachments($id: ID!) {
    internalWorkOrder(id: $id) {
      id
      attachments {
        ${fragment.ATTACHMENT_FRAGMENT}
      }
    }
  }
`;

export const ASSET_AUDIT_LOGS_QUERY = gql`
  query AssetAuditLogs($assetId: ID!, $action: String) {
    assetAuditLogs(assetId: $assetId, action: $action) {
      id
      itemId
      itemType
      details
      performedAt
      object
      performer {
        id
        email
      }
    }
  }
`;

export const CURRENT_KIT_RESERVATIONS_QUERY = gql`
  query CurrentKitReservations($assetId: ID!, $limit: Int!) {
    currentKitReservations(assetId: $assetId, limit: $limit) {
      collection {
        id
        quantity
        inventoryRequest {
          workOrderNumber
          requestedAt
          designer
          comment
        }
      }
    }
  }
`;

export const KIT_BY_ITEM_ID_QUERY = gql`
  query KitByItemId($itemId: String!) {
    kitByItemId(itemId: $itemId) {
      id
    }
  }
`;

export const ASSET_REPLAY_LOGS_QUERY = gql`
  query AssetReplayLogs($assetId: ID!, $page: Int, $limit: Int, $actionType: String) {
    assetReplayLogs(assetId: $assetId, page: $page, limit: $limit, actionType: $actionType) {
      collection {
        id
        assetId
        shipmentItemId
        performedAt
        actualReceivedDate
        user {
          email
        }
        availableQty
        warehouseQty
        allocatedQty
        inCartQty
        reservedQty
        woNumber
        rackLocationsWas
        rackLocationChanges
        rackLocations
        quantity
        action
        actionSource
        disabledForBilling
      }
      metadata {
        currentPage
        limitValue
        totalPages
        totalCount
      }
    }
  }
`;

export const LOCATION_TIMELINE_QUERY = gql`
  query LocationTimeline(
    $subjectType: String!
    $subjectId: ID!
    $dateRange: [String!]
    $actions: [String!]
    $page: Int
    $limit: Int
  ) {
    locationTimeline(
      subjectType: $subjectType
      subjectId: $subjectId
      dateRange: $dateRange
      actions: $actions
      page: $page
      limit: $limit
    ) {
      collection {
        id
        eventType
        source
        performedAt
        subQty
        occupiedAtTime
        assetIds
        metadata
        assetId
        itemId
        assetReplayLogId
        user {
          email
        }
        asset {
          id
          itemId
        }
        fromLocation {
          id
          locationName
          warehouse {
            id
            warehouseId
            name
          }
        }
        toLocation {
          id
          locationName
          warehouse {
            id
            warehouseId
            name
          }
        }
      }
      metadata {
        currentPage
        limitValue
        totalPages
        totalCount
      }
    }
  }
`;

export const ASSET_REPLAY_LOG_QUERY = gql`
  query AssetReplayLog($id: ID!) {
    assetReplayLog(id: $id) {
      id
      assetId
      performedAt
      warehouseQty
      rackLocations
    }
  }
`;

export const ASSET_CHARGES_INFO_QUERY = gql`
  query AssetChargesInfo($assetId: ID!) {
    asset(id: $assetId) {
      id
      itemId
      assetId
      description
      availableQuantity
      warehouseQuantity
      storageCharges
      taxCharges
      billingCharges
      palletType
      unitType
      weight
      squareFootage
      cubicFootage
      dimensions
      rackLocations
      billingType
      images {
        id
        url
        default
      }
      project {
        id
        name
      }
    }
  }
`;

export const PROJECT_BILLING_RATES_QUERY = gql`
  query ProjectBillingRates($projectId: ID!) {
    project(id: $projectId) {
      id
      billingRates
      palletTypes {
        ${fragment.PALLET_TYPE_FRAGMENT}
      }
      unitTypes {
        ${fragment.UNIT_TYPE_FRAGMENT}
      }
      squareFootageTypes {
        ${fragment.SQUARE_FOOTAGE_TYPE_FRAGMENT}
      }
    }
  }
`;

export const PROJECT_PALLET_TYPES_QUERY = gql`
  query ProjectPalletTypes($projectId: ID!) {
    project(id: $projectId) {
      id
      palletTypes {
        id
        name
        isActive
      }
    }
  }
`;

export const PROJECT_UNIT_TYPES_QUERY = gql`
  query ProjectUnitTypes($projectId: ID!) {
    project(id: $projectId) {
      id
      unitTypes {
        id
        name
        isActive
      }
    }
  }
`;

export const PROJECT_SQUARE_FOOTAGE_TYPES_QUERY = gql`
  query ProjectSquareFootageTypes($projectId: ID!) {
    project(id: $projectId) {
      id
      squareFootageTypes {
        id
        name
        isActive
      }
    }
  }
`;

export const ASSET_REPLAY_LOGS_COLLECTION_QUERY = gql`
  query AssetReplayLogsCollection(
    $dateRange: [String!]
    $searchTerm: String
    $page: Int
    $limit: Int
    $sortOrder: String
    $sortField: String
    $projectId: ID
    $clientId: ID
    $itemId: String
    $warehouseId: String
    $actionType: String
    $userEmail: String
    $excludeDeletedAssets: Boolean
  ) {
    assetReplayLogs(
      dateRange: $dateRange
      searchTerm: $searchTerm
      page: $page
      limit: $limit
      sortOrder: $sortOrder
      sortField: $sortField
      projectId: $projectId
      clientId: $clientId
      itemId: $itemId
      warehouseId: $warehouseId
      actionType: $actionType
      userEmail: $userEmail
      excludeDeletedAssets: $excludeDeletedAssets
    ) {
      collection {
        id
        assetId
        itemId
        performedAt
        user {
          email
        }
        availableQty
        warehouseQty
        allocatedQty
        inCartQty
        reservedQty
        woNumber
        rackLocations
        quantity
        action
        asset {
          projectId
        }
      }
      metadata {
        currentPage
        limitValue
        totalPages
        totalCount
      }
    }
  }
`;

export const ASSET_REPLAY_LOGS_WITH_DETAILS_QUERY = gql`
  query AssetReplayLogsWithDetails(
    $dateRange: [String!]
    $searchTerm: String
    $page: Int
    $limit: Int
    $sortOrder: String
    $sortField: String
    $projectId: ID
    $clientId: ID
    $itemId: String
    $warehouseId: String
    $actionType: String
    $userEmail: String
    $excludeDeletedAssets: Boolean
  ) {
    assetReplayLogs(
      dateRange: $dateRange
      searchTerm: $searchTerm
      page: $page
      limit: $limit
      sortOrder: $sortOrder
      sortField: $sortField
      projectId: $projectId
      clientId: $clientId
      itemId: $itemId
      warehouseId: $warehouseId
      actionType: $actionType
      userEmail: $userEmail
      excludeDeletedAssets: $excludeDeletedAssets
    ) {
      collection {
        id
        assetId
        itemId
        performedAt
        user {
          email
          name
        }
        availableQty
        warehouseQty
        allocatedQty
        inCartQty
        reservedQty
        woNumber
        rackLocations
        rackLocationChanges
        quantity
        action
        asset {
          id
          itemId
          description
          rackLocations
          availableQuantity
          warehouseQuantity
          project {
            id
            name
          }
          client {
            id
            name
          }
        }
      }
      metadata {
        currentPage
        limitValue
        totalPages
        totalCount
      }
    }
  }
`;

export const WAREHOUSE_LOCATION_AUDITS_QUERY = gql`
  query GetWarehouseLocationAudits(
    $warehouseLocationId: ID
    $mobileWarehouseLocationId: ID
    $page: Int
    $limit: Int
  ) {
    warehouseLocationAudits(
      warehouseLocationId: $warehouseLocationId
      mobileWarehouseLocationId: $mobileWarehouseLocationId
      page: $page
      limit: $limit
    ) {
      collection {
        id
        status
        startedAt
        completedAt
        durationSeconds
        auditedBy {
          id
          name
          email
        }
        auditItems {
          id
          status
          preAuditQuantity
          postAuditQuantity
          asset {
            id
            itemId
            description
          }
        }
      }
      metadata {
        currentPage
        totalPages
        totalCount
      }
    }
  }
`;

export const GET_LOCATIONS_QUERY = gql`
  query GetLocations {
    locations {
      ${fragment.LOCATION_FRAGMENT}
    }
  }
`;

export const GET_REQUESTORS_QUERY = gql`
  query GetRequestors {
    requestors {
      ${fragment.REQUESTOR_FRAGMENT}
    }
  }
`;

export const GET_PLANS_QUERY = gql`
  query EquipmentPlannerFloorPlans($projectId: ID!, $clientId: ID!) {
    equipmentPlannerFloorPlans(projectId: $projectId, clientId: $clientId) {
      id
      name
      metadata
    }
  }
`;

export const GET_PLAN_QUERY = gql`
  #Query used to upload new plan
  query EquipmentPlannerFloorPlans($floorPlanId: ID!) {
    equipmentPlannerFloorPlan(floorPlanId: $floorPlanId) {
      id
      state
      iconSet {
        id
      }
      metadata
    }
  }
`;

export const GET_PLAN_QUERY_ADMIN = gql`
  #Query used to fetch plan for admin view
  query EquipmentPlannerFloorPlans($floorPlanId: ID!) {
    equipmentPlannerFloorPlan(floorPlanId: $floorPlanId) {
      id
      iconSet {
        id
      }
      metadata
    }
  }
`;

export const GET_PLAN_VIEWS_QUERY = gql`
  query EquipmentPlannerPlanViews($floorPlanId: ID!) {
    equipmentPlannerPlanViews(floorPlanId: $floorPlanId) {
      id
      name
      floorPlanState
      iconSetState
      accessLink {
        id
        token
      }
    }
  }
`;

export const WAREHOUSE_LOCATIONS = gql`
  query Warehouse($id: ID) {
    warehouse(id: $id) {
      name
      warehouseId
      warehouseLocations {
        ${fragment.WAREHOUSE_LOCATION}
      }
    }
  }
`;

export const WAREHOUSE_INFO = gql`
  query Warehouse($id: ID) {
    warehouse(id: $id) {
      id
      name
      warehouseId
    }
  }
`;

export const WAREHOUSE_PROJECTS = gql`
  query Warehouse($id: ID) {
    warehouse(id: $id) {
      id
      name
      warehouseId
      projects {
        id
        name
      }
    }
  }
`;

export const WAREHOUSES_AND_PROJECTS = gql`
  query Warehouses($userWarehouses: Boolean) {
    warehouses(userWarehouses: $userWarehouses) {
      id
      warehouseId
      name
      projects {
        id
        name
      }
    }
  }
`;

export const WAREHOUSE_LOCATIONS_COLLECTION = gql`
  query WarehouseLocationsCollection(
    $warehouseId: ID
    $search: String
    $projectIds: [ID!]
    $occupied: Boolean
    $active: Boolean
    $height: JSON
    $locationType: String
    $excludeType: String
    $limit: Int
    $page: Int
    $order: JSON
    $clientId: ID
    $skipVirtual: Boolean
    $matchLocationName: Boolean
    $expandSlotSiblings: Boolean
    $verifyUnoccupied: Boolean
  ) {
    warehouseLocationsCollection(
      warehouseId: $warehouseId
      search: $search
      projectIds: $projectIds
      occupied: $occupied
      active: $active
      height: $height
      locationType: $locationType
      excludeType: $excludeType
      limit: $limit
      page: $page
      order: $order
      clientId: $clientId
      skipVirtual: $skipVirtual
      matchLocationName: $matchLocationName
      expandSlotSiblings: $expandSlotSiblings
      verifyUnoccupied: $verifyUnoccupied
    ) {
      collection {
        ... on WarehouseLocation {
          id
          locationId
          locationName
          slotGroupKey
          zone
          locationType
          occupied
          active
          dimensions
          sqFtOfSlot
        }
        ... on MobileWarehouseLocation {
          id
          locationId
          locationName
          zone
          locationType
          occupied
          active
          dimensions
          sqFtOfSlot
          permanentWarehouseLocationAssignment
          warehouseLocations {
            id
            locationName
          }
        }
      }
      metadata {
        occupiedCount
        totalCount
        baseOccupiedCount
        baseTotalCount
        totalPages
        currentPage
        totalIds
      }
    }
  }
`;

export const WAREHOUSE_LOCATIONS_COLLECTION_INVENTORY_TRANSACTION = gql`
  query WarehouseLocationsCollection(
    $warehouseId: ID
    $search: String
    $projectIds: [ID!]
    $occupied: Boolean
    $active: Boolean
    $height: JSON
    $locationType: String
    $excludeType: String
    $limit: Int
    $page: Int
    $order: JSON
    $clientId: ID
    $projectId: ID
    $skipVirtual: Boolean
    $matchLocationName: Boolean
    $expandSlotSiblings: Boolean
  ) {
    warehouseLocationsCollection(
      warehouseId: $warehouseId
      search: $search
      projectIds: $projectIds
      occupied: $occupied
      active: $active
      height: $height
      locationType: $locationType
      excludeType: $excludeType
      limit: $limit
      page: $page
      order: $order
      clientId: $clientId
      projectId: $projectId
      skipVirtual: $skipVirtual
      matchLocationName: $matchLocationName
      expandSlotSiblings: $expandSlotSiblings
    ) {
      collection {
        ... on WarehouseLocation {
          id
          locationId
          locationName
          slotGroupKey
          slot
          warehouse {
            id
            warehouseId
            name
          }
        }
        ... on MobileWarehouseLocation {
          id
          locationId
          locationName
          permanentWarehouseLocationAssignment
          warehouse {
            id
            warehouseId
            name
          }
          warehouseLocations {
            id
            locationName
            slotGroupKey
          }
        }
      }
      metadata {
        occupiedCount
        totalCount
        totalPages
        currentPage
      }
    }
  }
`;

export const WAREHOUSE_LOCATION = gql`
  query WarehouseLocation($id: ID!) {
    warehouseLocation(id: $id) {
       ${fragment.WAREHOUSE_LOCATION_WITH_IMAGES}
    }
  }
`;

export const WAREHOUSE_LOCATION_MISSING_CENTERS = gql`
  query WarehouseLocationMissingCenters($warehouseId: ID!) {
    warehouseLocationMissingCenters(warehouseId: $warehouseId) {
      baseName
      proposedLocationId
      proposedLocationName
      leftLocation {
        id
        locationId
        locationName
        active
      }
      rightLocation {
        id
        locationId
        locationName
        active
      }
    }
  }
`;

export const MOBILE_WAREHOUSE_LOCATION = gql`
  query MobileWarehouseLocation($id: ID!) {
    mobileWarehouseLocation(id: $id) {
      id
      locationId
      locationName
      searchTerms
      zone
      locationType
      sqFtOfSlot
      dimensions
      active
      mobileLocationImages {
        ${fragment.WAREHOUSE_LOCATION_IMAGE_FRAGMENT}
      }
      occupied
      createdBy {
        id
        email
      }
      warehouse {
        id
        warehouseId
        name
      }
      warehouseLocations { 
        id
        locationName
        slotGroupKey
        warehouse {
          warehouseId
          name
        }
      }
    }
  } 
`;

export const WAREHOUSE_NAMES = gql`
  query Warehouses($userWarehouses: Boolean) {
    warehouses(userWarehouses: $userWarehouses) {
      id
      warehouseId
      name
    }
  }
`;

export const PROJECTS_DATA_QUERY = gql`
  query FetchProjectsData($projectIds: [ID!], $storePage: Boolean) {
    projectsData(projectIds: $projectIds, storePage: $storePage) {
      projects {
        id
        name
      }
      warehouses {
        id
        warehouseId
        name
      }
      costCenters {
        id
        costCenterName
        costCenterCode
        comments
      }
    }
  }
`;

export const WAREHOUSES_SETTINGS = gql`
  query WarehousesSettings($userWarehouses: Boolean) {
    warehouses(userWarehouses: $userWarehouses) {
      id
      warehouseId
      name
      warehouseEmailSendList
      salesTaxRate
      address {
        id
        street
        street2
        city
        state
        zipCode
      }
      createdAt
      updatedAt
    }
  }
`;

export const WAREHOUSE_SETTINGS_DETAIL = gql`
  query WarehouseSettingsDetail($id: ID) {
    warehouse(id: $id) {
      id
      warehouseId
      name
      warehouseEmailSendList
      salesTaxRate
      excludeFromBilling
      address {
        id
        street
        street2
        city
        state
        zipCode
        addressName
      }
      warehouseLocationsCount
      mobileWarehouseLocationsCount
      company {
        id
        name
      }
      createdAt
      updatedAt
    }
  }
`;

export const WAREHOUSE_LOCATIONS_DETAILS = gql`
  query WarehouseLocationsDetails($warehouseId: ID!) {
    warehouseLocationsDetails(warehouseId: $warehouseId) {
      rackEmpty
      rackOccupied
      vaultEmpty
      vaultOccupied
      palletEmpty
      palletOccupied
      floorEmpty
      floorOccupied
      trailerEmpty
      trailerOccupied
      sofaRackEmpty
      sofaRackOccupied
      mobileVaultEmpty
      mobileVaultOccupied
      mobilePalletEmpty
      mobilePalletOccupied
      mobileVirtualPalletEmpty
      mobileVirtualPalletOccupied
      mobileTrailerEmpty
      mobileTrailerOccupied
      totalEmptyBaseLocations
      totalOccupiedBaseLocations
      totalEmptyMobileLocations
      totalOccupiedMobileLocations
    }
  }
`;

export const SUSTAINABILITY_SHEET_DATA_QUERY = gql`
  query sustainabilitySheetData {
    sustainabilitySheetData {
      category
      subcategory
      unitCost
      unitWeight
      unitCo2
    }
  }
`;

export const PROJECT_BY_ID = gql`
  query Project($id: ID!, $userType: String) {
    project(id: $id) {
      id
      settings
      users(userType: $userType) {
        id
        name
        email
        currentSignInAt
        role
        projectCostCenters(projectId: $id) {
          id
          costCenterName
        }
      }
    }
  }
`;

export const GET_SHIPMENT_QUERY = gql`
  query Shipment($id: ID!) {
    shipment(id: $id) {
      ${fragment.SHIPMENT_FRAGMENT}
      shipmentItems {
        ${fragment.SHIPMENT_ITEM_FRAGMENT}
      }
      overagesShortagesDamages {
        id
        osdType
        osdNotes
        osdQuantity
        shipmentItem {
          ${fragment.SHIPMENT_ITEM_FRAGMENT}
        }
        attachments {
          ${fragment.ATTACHMENT_FRAGMENT}
        }
      }
    }
  }
`;

export const CALENDAR_SHIPMENTS_QUERY = gql`
  query CalendarShipments($projectId: ID, $clientId: ID, $warehouseId: String, $dateRange: JSON, $limit: Int) {
    shipments(projectId: $projectId, clientId: $clientId, warehouseId: $warehouseId, dateRange: $dateRange, limit: $limit) {
      collection {
        ${fragment.SIMPLE_CALENDAR_SHIPMENT_FRAGMENT}
      }
      metadata {
        ${fragment.PAGINATION_FRAGMENT}
      }
    }
  }
`;

export const GET_SHIPMENTS_QUERY = gql`
  query Shipments($projectId: ID, $clientId: ID, $warehouseId: String, $dateRange: JSON, $search: String, $itemId: String, $limit: Int, $page: Int, $order: JSON, $missingApprovedNotification: Boolean, $missingCompleteTime: Boolean, $hasOsd: Boolean) {
    shipments(projectId: $projectId, clientId: $clientId, warehouseId: $warehouseId, dateRange: $dateRange, search: $search, itemId: $itemId, limit: $limit, page: $page, order: $order, missingApprovedNotification: $missingApprovedNotification, missingCompleteTime: $missingCompleteTime, hasOsd: $hasOsd) {
      collection {
        ${fragment.SHIPMENT_FRAGMENT}
      }
      metadata {
        ${fragment.PAGINATION_FRAGMENT}
      }
    }
  }
`;

export const PROJECT_BILLING_TOTALS_SUMMARY_QUERY = gql`
  query ProjectBillingTotalsSummary($projectId: ID!, $startDate: ISO8601Date, $endDate: ISO8601Date, $categories: [String!]) {
    projectBillingTotalsSummary(projectId: $projectId, startDate: $startDate, endDate: $endDate, categories: $categories) {
      projectId
      projectName
      allTime
      startDate
      endDate
      grandTotal
      inboundDetail {
        billingType
        shipmentCount
        totalWeight
        nonMinimumChargeWeight
        minimumChargeShipmentCount
        nonMinimumChargeTotal
        minimumChargeTotal
        inboundHundredweightRate
        inboundMinimumChargeRate
        totalInboundCharges
      }
      sections {
        key
        title
        recordLabel
        recordCount
        billingType
        totalCharges
        rows {
          label
          quantity
          quantityUnit
          rate
          total
          note
        }
      }
    }
  }
`;

export const GET_APPROVAL_SHIPMENTS_QUERY = gql`
  query ApprovalShipments($projectId: ID, $clientId: ID, $warehouseId: String, $dateRange: JSON, $search: String, $limit: Int, $page: Int, $order: JSON, $missingApprovedNotification: Boolean) {
    shipments(projectId: $projectId, clientId: $clientId, warehouseId: $warehouseId, dateRange: $dateRange, search: $search, limit: $limit, page: $page, order: $order, missingApprovedNotification: $missingApprovedNotification) {
      collection {
        ${fragment.SHIPMENT_APPROVAL_TABLE_FRAGMENT}
      }
      metadata {
        ${fragment.PAGINATION_FRAGMENT}
      }
    }
  }
`;

export const GET_SHIPMENTS_COUNT_QUERY = gql`
  query ShipmentsCount(
    $projectId: ID
    $clientId: ID
    $warehouseId: String
    $dateRange: JSON
    $search: String
    $limit: Int
    $page: Int
    $order: JSON
    $missingApprovedNotification: Boolean
    $missingCompleteTime: Boolean
  ) {
    shipments(
      projectId: $projectId
      clientId: $clientId
      warehouseId: $warehouseId
      dateRange: $dateRange
      search: $search
      limit: $limit
      page: $page
      order: $order
      missingApprovedNotification: $missingApprovedNotification
      missingCompleteTime: $missingCompleteTime
    ) {
      metadata {
        totalCount
      }
    }
  }
`;

export const GET_ADDRESSES_BY_PROJECT_IDS = gql`
  query GetAddresses($entityIds: [ID!]!) {
    addresses(entityName: "project", entityIds: $entityIds) {
      id
      ${fragment.ADDRESS_FRAGMENT}
    }
  }
`;

export const MASTER_SEARCH_QUERY = gql`
  query MasterSearch($search: String!, $limit: Int, $page: Int) {
    masterSearchCollection(search: $search, limit: $limit, page: $page) {
      collection {
        ... on Project {
          id
          name
          customerLogoUrl
        }
        ... on Shipment {
          id
          shipmentId
          notes
          attachments {
            url
          }
          project {
            id
            name
          }
        }
        ... on Asset {
          id
          itemId
          description
          images {
            url
          }
          project {
            id
          }
        }
      }
      metadata {
        totalCount
        totalPages
        currentPage
      }
    }
  }
`;

export const INTERNAL_WORK_ORDERS_QUERY = gql`
  query InternalWorkOrders($limit: Int, $page: Int, $projectId: Int, $search: String) {
    internalWorkOrders(page: $page, limit: $limit, projectId: $projectId, search: $search) {
      collection {
        id
        uid
        internalWorkOrderNumber
        revisionNumber
        comment
        deadlineDate
        createdAt
        user {
          id
          email
        }
        project {
          id
          name
        }
        internalWorkOrderItems {
          id
          quantity
        }
        billingCharges
        vaStatus
        externalRecords {
          id
          recordType
          externalId
          data
        }
      }
      metadata {
        ${fragment.PAGINATION_FRAGMENT}
      }
    }
  }
`;

export const SHOPPING_INTERNAL_WORK_ORDERS_QUERY = gql`
  query ShoppingInternalWorkOrders($limit: Int, $page: Int, $projectId: Int, $search: String) {
    shoppingInternalWorkOrders(
      page: $page
      limit: $limit
      projectId: $projectId
      search: $search
    ) {
      collection {
        id
        uid
        internalWorkOrderNumber
        revisionNumber
        comment
        deadlineDate
        createdAt
        user {
          id
          email
        }
        project {
          id
          name
        }
        internalWorkOrderItems {
          id
          quantity
        }
        vaStatus
      }
      metadata {
        ${fragment.PAGINATION_FRAGMENT}
      }
    }
  }
`;

export const INTERNAL_WORK_ORDER_QUERY = gql`
  query InternalWorkOrder($id: ID!) {
    internalWorkOrder(id: $id) {
      ${fragment.OPTIMIZED_INTERNAL_WORK_ORDER_FRAGMENT}
    }
  }
`;

export const WORK_ORDERS_QUERY = gql`
  query WorkOrders(
    $limit: Int
    $page: Int
    $projectId: Int
    $clientId: Int
    $warehouseId: String
    $dateRange: JSON
    $sortField: String
    $sortDirection: String
    $type: String
    $search: String
    $orderType: String
    $statusFilter: String
  ) {
    workOrders(
      limit: $limit
      page: $page
      projectId: $projectId
      clientId: $clientId
      warehouseId: $warehouseId
      dateRange: $dateRange
      sortField: $sortField
      sortDirection: $sortDirection
      type: $type
      search: $search
      orderType: $orderType
      statusFilter: $statusFilter
    ) {
      collection {
        __typename
        ... on InventoryRequest {
          ${fragment.SIMPLE_INVENTORY_REQUEST_FRAGMENT}
        }
        ... on InternalWorkOrder {
          id
          uid
          internalWorkOrderNumber
          comment
          deadlineDate
          createdAt
          vaStatus
          user { id email }
          project { id name }
          warehouse { id warehouseId name }
        }
      }
      metadata {
        ${fragment.PAGINATION_FRAGMENT}
      }
    }
  }
`;

export const CALENDAR_WORK_ORDERS_QUERY = gql`
  query WorkOrders(
    $limit: Int
    $page: Int
    $projectId: Int
    $clientId: Int
    $warehouseId: String
    $dateRange: JSON
    $sortField: String
    $sortDirection: String
    $type: String
    $search: String
    $orderType: String
  ) {
    workOrders(
      limit: $limit
      page: $page
      projectId: $projectId
      clientId: $clientId
      warehouseId: $warehouseId
      dateRange: $dateRange
      sortField: $sortField
      sortDirection: $sortDirection
      type: $type
      search: $search
      orderType: $orderType
    ) {
      collection {
        __typename
        ... on InventoryRequest {
          ${fragment.SIMPLE_CALENDAR_INVENTORY_REQUEST_FRAGMENT}
        }
        ... on InternalWorkOrder {
          id
          uid
          internalWorkOrderNumber
          comment
          deadlineDate
          createdAt
          vaStatus
          user { id email }
        }
      }
      metadata {
        ${fragment.PAGINATION_FRAGMENT}
      }
    }
  }
`;

export const ASSETS_COLLECTION_STORE_QUERY = gql`
  query GetAssetsStoreCollection(
    $clientId: ID
    $projectId: ID
    $inventoryRequestId: ID
    $search: String
    $categories: [String!]
    $page: Int
    $limit: Int
    $order: JSON
    $storePage: Boolean
    $type: String
    $types: [String!]
    $warehouseIds: [String!]
    $warehouseLocationName: String
    $mobileWarehouseLocationName: String
    $hasImportedAssets: Boolean
    $showDeployedAssets: Boolean
    $excludeReceivedAssets: Boolean
    $itemId: String
    $hideDepletedAssets: Boolean
    $hideVariants: Boolean
    $includeRegularAssetsFromGroupedProjects: Boolean
    $costCenterIds: [ID!]
    $hasReservedQuantity: Boolean
  ) {
    assetsCollection(
      clientId: $clientId
      projectId: $projectId
      inventoryRequestId: $inventoryRequestId
      search: $search
      categories: $categories
      page: $page
      limit: $limit
      order: $order
      storePage: $storePage
      type: $type
      types: $types
      warehouseIds: $warehouseIds
      warehouseLocationName: $warehouseLocationName
      mobileWarehouseLocationName: $mobileWarehouseLocationName
      hasImportedAssets: $hasImportedAssets
      showDeployedAssets: $showDeployedAssets
      excludeReceivedAssets: $excludeReceivedAssets
      itemId: $itemId
      hideDepletedAssets: $hideDepletedAssets
      hideVariants: $hideVariants
      includeRegularAssetsFromGroupedProjects: $includeRegularAssetsFromGroupedProjects
      costCenterIds: $costCenterIds
      hasReservedQuantity: $hasReservedQuantity
    ) {
      collection {
        ${fragment.ASSET_FRAGMENT}
        ${fragment.ASSET_MEDICAL_FRAGMENT}
        isVariant
      }
      metadata {
        totalPages
        totalCount
        currentPage
        limitValue
      }
    }
    categories(projectId: $projectId)
  }
`;

export const ASSET_INVENTORY_ITEMS_QUERY = gql`
  query GetAsset($id: ID!) {
    asset(id: $id) {
      inventoryItems {
        quantity
        inventoryRequest {
          workOrderNumber
        }
      }
    }
  }
`;

export const ASSETS_REMOVAL_CONFLICTS_QUERY = gql`
  query AssetsRemovalConflicts($ids: [ID!]!) {
    assetsRemovalConflicts(ids: $ids) {
      itemId
      orderId
      orderNumber
    }
  }
`;

export const ASSET_IN_CART_QUANTITY_DATA_QUERY = gql`
  query AssetInCartQuantityData($assetId: ID!) { 
    assetInCartQuantityData(assetId: $assetId) {
      quantity
      users { 
        id
        email 
        inventoryItems {
          id
          quantity
          asset {
            id
            ${fragment.ASSET_FRAGMENT}
          }
        }
        kitInventoryItems {
          id
          quantity
          asset {
            id
            ${fragment.ASSET_FRAGMENT}
          }
          kit {
            id
            ${fragment.ASSET_FRAGMENT}
          }
        }
      }
    }
  }
`;

export const PROJECT_IN_CART_QUANTITY_DATA_QUERY = gql`
  query ProjectInCartQuantityData($projectId: ID!) {
    projectInCartQuantityData(projectId: $projectId) {
      asset {
        id
        itemId
        description
        model
        images {
          id
          url
          default
        }
        project {
          id
        }
      }
      user {
        id
        email
        name
      }
      quantity
      addedAt
    }
  }
`;

export const CUSTOM_FIELD_TEMPLATES_QUERY = gql`
  query CustomFieldTemplates($projectId: ID, $sortBy: String, $sortDirection: String) {
    customFieldTemplates(projectId: $projectId, sortBy: $sortBy, sortDirection: $sortDirection) {
      ${fragment.CUSTOM_FIELD_TEMPLATE_FRAGMENT}
    }
  }
`;

export const SHARED_CLIENT_PROJECTS_QUERY = gql`
  query SharedClientProjects($projectId: ID!) {
    sharedClientProjects(projectId: $projectId) {
      id
      client {
        id
        name
      }
    }
  }
`;

export const SCHEDULERS_QUERY = gql`
  query Schedulers {
    schedulers {
      ${fragment.SCHEDULER_FRAGMENT}
    }
  }
`;

export const ASSETS_BY_IDS_WITH_QUANTITY_QUERY = gql`
  query GetAssetsByIdsWithQuantity($assetIds: [ID!]!) {
    assetsCollection(assetIds: $assetIds) {
      collection {
        id
        assetId
        itemId
        description
        images {
          ${fragment.ASSET_IMAGE_FRAGMENT}
        }
        availableQuantity
      }
    }
  }
`;

export const REVISION_LOGS_QUERY = gql`
  query RevisionLogs($itemId: ID!, $itemType: String!) {
    revisionLogs(itemId: $itemId, itemType: $itemType) {
      id
      itemId
      itemType
      childItemId
      childItemType
      performer {
        id
        email
        name
      }
      action
      performedAt
      notes
      createdAt
      updatedAt
      object
      objectWas
      details
      diff
      summaryPretty
      diffPretty
    }
  }
`;

export const BILLING_REPORTS_QUERY = gql`
  query BillingReports(
    $projectId: ID
    $startDate: String!
    $endDate: String!
    $page: Int
    $limit: Int
  ) {
    billingReports(
      projectId: $projectId
      startDate: $startDate
      endDate: $endDate
      page: $page
      limit: $limit
    ) {
      collection {
        id
        periodStart
        periodEnd
        version
        reportUrl
        chargeSummary
        fingerprint
        createdAt
        updatedAt
        project {
          id
          name
        }
        favoriteReport {
          id
          payload
        }
      }
      metadata {
        ${fragment.PAGINATION_FRAGMENT}
      }
    }
  }
`;

export const BILLING_PERIODS_QUERY = gql`
  query BillingPeriods($projectId: ID!, $year: Int, $limit: Int) {
    project(id: $projectId) {
      id
      name
      billingEmailSendList
      billingMethod
      billingDate
      billingAnchorDate
      client {
        id
        name
        clientBillingEmailSendList
        company {
          id
          emailsSendList
        }
      }
      billingPeriods(year: $year, limit: $limit) {
        ${fragment.BILLING_PERIOD_FRAGMENT}
      }
    }
  }
`;

export const GET_INBOUND_LOCATION_ITEMS_QUERY = gql`
  query GetInboundLocationItems(
    $warehouseLocationType: String
    $warehouseId: String
    $projectId: ID
    $clientId: ID
    $search: String
    $page: Int
    $limit: Int
    $order: JSON
  ) {
    assetsCollection(
      warehouseLocationType: $warehouseLocationType
      warehouseId: $warehouseId
      projectId: $projectId
      clientId: $clientId
      search: $search
      page: $page
      limit: $limit
      order: $order
    ) {
      collection {
        id
        itemId
        description
        rackLocations
        project {
          id
          name
        }
      }
      metadata {
        totalCount
        totalPages
        currentPage
      }
    }
  }
`;

export const DELIVERIES_QUERY = gql`
  query Deliveries($limit: Int, $page: Int, $search: String, $sortBy: [JSON!], $dateRange: JSON, $projectId: Int, $clientId: Int, $vaStatus: String) {
    deliveries(page: $page, limit: $limit, search: $search, sortBy: $sortBy, dateRange: $dateRange, projectId: $projectId, clientId: $clientId, vaStatus: $vaStatus) {
      collection {
        ${fragment.SIMPLE_DELIVERY_FRAGMENT}
      }
      metadata {
        ${fragment.PAGINATION_FRAGMENT}
      }
    }
  }
`;

export const DELIVERY_QUERY = gql`
  query Delivery($id: ID!) {
    delivery(id: $id) {
      ${fragment.DELIVERY_FRAGMENT}
    }
  }
`;

export const DELIVERY_ATTACHMENTS_QUERY = gql`
  query DeliveryAttachments($id: ID!) {
    delivery(id: $id) {
      id
      attachments {
        ${fragment.ATTACHMENT_FRAGMENT}
      }
    }
  }
`;

export const COMPANY_CONFIGURATION_CATALOG_QUERY = gql`
  query CompanyConfigurationCatalog {
    user {
      id
      company {
        id
        settings
        configurationCatalog
        emailsSendList
        labelsLogoUrl
        secondaryLogoUrl
        subsidiaryLogoUrl
        subsidiary2LogoUrl
        blackoutDates
        enabledBillingTypes
        taxableChargeTypes
        storageCalculationMethod
      }
    }
  }
`;

export const TRANSFER_SHIPMENT_TARGET_QUERY = gql`
  query TransferShipmentTarget($id: ID!) {
    shipment(id: $id) {
      id
      shipmentId
      project {
        id
        client {
          id
        }
      }
    }
  }
`;
